import React from "react";

import { ChevronDown, ChevronUp, Printer } from "lucide-react";

import { Checkbox } from "~/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { useFacturaNumbers } from "~/pages/operations/hooks/useFacturaNumbers";
import { SalesRequest } from "~/types/client";
import { formatCurrency } from "~/utils/helpers";

export type SortField =
  | "facturaNumber"
  | "clientName"
  | "sellerName"
  | "createdAt"
  | "deliveryStreet"
  | "totalAmount";

export interface SortConfig {
  field: SortField | null;
  direction: "asc" | "desc";
}

interface TableProps {
  orders: SalesRequest[];
  getClientName: (clientId: string) => string;
  getSellerInfo: (userId: string) => { name: string; lastname: string };
  statusBadge: React.ReactNode;
  showActions: boolean;
  actions: (order: SalesRequest) => React.ReactNode;
  onRowDetailClick?: (order: SalesRequest) => void;
  selectedOrderIds?: Set<string>;
  onSelectOrder?: (orderId: string) => void;
  isAllSelected?: boolean;
  onSelectAll?: () => void;
  showCheckboxes?: boolean;
  currentTab?: string;
  sortConfig?: SortConfig;
  onSort?: (field: SortField) => void;
}

export function OperationsOrdersTable({
  orders,
  getClientName,
  getSellerInfo,
  statusBadge,
  showActions,
  actions,
  onRowDetailClick,
  selectedOrderIds,
  onSelectOrder,
  isAllSelected,
  onSelectAll,
  showCheckboxes = false,
  currentTab,
  sortConfig,
  onSort,
}: TableProps) {
  const { getFacturaNumber } = useFacturaNumbers(orders);

  const SortableHeader: React.FC<{
    field: SortField;
    children: React.ReactNode;
  }> = ({ field, children }) => {
    const isActive = sortConfig?.field === field;
    const direction = isActive ? sortConfig?.direction : null;

    return (
      <th
        className="cursor-pointer select-none p-2 hover:bg-gray-50"
        onClick={() => onSort?.(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <div className="flex flex-col">
            <ChevronUp
              className={`h-3 w-3 ${isActive && direction === "asc" ? "text-blue-600" : "text-gray-400"}`}
            />
            <ChevronDown
              className={`-mt-1 h-3 w-3 ${isActive && direction === "desc" ? "text-blue-600" : "text-gray-400"}`}
            />
          </div>
        </div>
      </th>
    );
  };

  if (orders.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full rounded-lg border border-gray-200 bg-white text-sm">
        <thead>
          <tr className="bg-muted/50">
            {showCheckboxes && (
              <th className="w-12 p-2">
                <Checkbox
                  checked={isAllSelected}
                  onCheckedChange={() => onSelectAll?.()}
                  aria-label="Seleccionar todos"
                />
              </th>
            )}
            <SortableHeader field="facturaNumber">Nº Factura</SortableHeader>
            <SortableHeader field="clientName">Cliente</SortableHeader>
            <SortableHeader field="sellerName">Vendedor</SortableHeader>
            <SortableHeader field="createdAt">Creada</SortableHeader>
            <SortableHeader field="deliveryStreet">Dirección</SortableHeader>
            <SortableHeader field="totalAmount">Monto</SortableHeader>
            <th className="p-2">Estado</th>
            {showActions && <th className="p-2">Acciones</th>}
            <th className="p-2">Ver detalles</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order, index) => (
            <tr
              key={order.id}
              style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
              className="border-b transition duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/30"
            >
              {showCheckboxes && (
                <td className="p-2">
                  <Checkbox
                    checked={selectedOrderIds?.has(order.id) ?? false}
                    onCheckedChange={() => onSelectOrder?.(order.id)}
                    aria-label={`Seleccionar pedido ${getFacturaNumber(order.id)}`}
                  />
                </td>
              )}
              <td className="p-2 font-mono">
                <div className="flex items-center gap-1">
                  {getFacturaNumber(order.id)}
                  {order.invoice_printed_at &&
                    (currentTab === "facturado" || currentTab === "in_transit") && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Printer className="h-3 w-3 text-cyan-600" />
                          </TooltipTrigger>
                          <TooltipContent>
                            Impreso:{" "}
                            {new Date(order.invoice_printed_at).toLocaleString()}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                </div>
              </td>
              <td className="p-2">
                {getClientName(order.clientId) || "Sin nombre"}
              </td>
              <td className="p-2">
                {getSellerInfo(order.createdBy).name}{" "}
                {getSellerInfo(order.createdBy).lastname}
              </td>
              <td className="p-2">
                {order.createdAt
                  ? new Date(order.createdAt).toLocaleDateString()
                  : "-"}
              </td>
              <td className="p-2">
                {order.deliveryStreet || "No especificada"}
              </td>
              <td className="p-2 font-bold">
                {formatCurrency(order.totalAmount)}
              </td>
              <td className="p-2">{statusBadge}</td>
              {showActions && <td className="p-2">{actions(order)}</td>}
              <td className="p-2">
                <button
                  className="inline-flex items-center gap-1 rounded border-2 border-blue-100 bg-transparent px-2 py-1 font-semibold text-blue-700 transition-colors hover:border-blue-300 hover:text-blue-900"
                  onClick={() => onRowDetailClick && onRowDetailClick(order)}
                  title="Ver detalles"
                >
                  <span>Ver detalles</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12H9m6 0l3 3-3 3m6-3H3"
                    />
                  </svg>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
